import * as d3 from 'd3';
import moment from 'moment';
import { css, svg } from '../../utility/lit-entity';
import { DeviceMonitoringChartLayerMixin } from './device-monitoring-chart-layer-mixin';
import '../../common/bn-icon';
import './device-events';

const STROKE_WIDTHS = [5, 7, 9];
const POINT_RADIUS = [6, 7, 8];
const PATH_TOP_MARGIN = 10;
const PATH_BOTTOM_MARGIN = 5;

export const DeviceMonitoringChartLayerLineMixin = (superClass) => class extends DeviceMonitoringChartLayerMixin(superClass) {
  static get properties() {
    return {
      dataKey: { type: String },
      maxValue: { type: Number },
      minValue: { type: Number },
      warningValue: { type: Number },
      alertValue: { type: Number },
    };
  }

  static get styles() {
    return [...super.styles, css`
      .path {
        fill: transparent;
      }
      .path.shadow {
        filter: url('#drop-shadow');
        clip-path: url(#any-path);
        stroke: rgba(0, 0, 0, 0.25);
        stroke-width: 5;
      }
      .path.good {
        stroke: var(--good-stroke, var(--indigo-50));
      }
      .path.warning {
        stroke: var(--warning-stroke, var(--indigo-50));
      }
      .path.alert {
        stroke: var(--alert-stroke, var(--indigo-60));
      }
      .point {
        stroke: transparent;
        opacity: 0;
      }
      .point.hovered {
        opacity: 1;
      }
      .point.good {
        fill: var(--good-stroke, var(--indigo-50));
      }
      .point.warning {
        fill: var(--warning-stroke, var(--indigo-50));
      }
      .point.alert {
        fill: var(--alert-stroke, var(--indigo-60));
      }
      .point.shadow {
        fill: rgba(0, 0, 0, 0.25);
        filter: url(#drop-shadow);
      }
    `];
  }

  renderLayerSVG() {
    const { REPORT_CADENCE_S } = this.constants();
    if (!this.events || this.events.length === 0) {
      return svg``;
    }

    const scaledData = this.parseData(this.events);
    if (scaledData.length === 0) {
      return svg``;
    }

    scaledData.unshift({
      ...scaledData[0],
      x: this.xScale(moment(scaledData[0].event.date).add(REPORT_CADENCE_S, 'seconds')),
      composed: true,
    });
    scaledData.push({
      ...scaledData[scaledData.length - 1],
      x: this.xScale(moment(scaledData[scaledData.length - 1].event.date).subtract(REPORT_CADENCE_S, 'seconds')),
      composed: true,
    });

    return svg`
      <filter id="drop-shadow" x="-100%" y="-100%" width="200%" height="200%" filterUnits="userSpaceOnUse">
        <feGaussianBlur in="SourceAlpha" stdDeviation="1" />
        <feOffset dx="0" dy="3" />
        <feMerge>
            <feMergeNode />
            <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>

      ${this._renderClipPaths(scaledData)}
      ${this._renderPaths(scaledData)}
      ${this._renderPointIndicators(scaledData)}
    `;
  }

  parseData(events) {
    const yScale = d3.scaleLinear()
      .domain([this.minValue, this.maxValue])
      .range([this.barHeight - PATH_BOTTOM_MARGIN, PATH_TOP_MARGIN]);

    return events.map((event) => {
      if (event[this.dataKey] === undefined) {
        return;
      }

      let warningLevel = 0;
      if (event[this.dataKey] < this.alertValue) {
        warningLevel = 2;
      } else if (event[this.dataKey] < this.warningValue) {
        warningLevel = 1;
      }

      return {
        x: this.xScale(event.date),
        y: Math.min(yScale(event[this.dataKey]), this.barHeightPadded),
        warningLevel,
        event,
      };
    }).filter((data) => data);
  }

  _renderPaths(scaledData) {
    return svg`
      <g class="paths">
        ${this._renderPathShadow(scaledData)}
        ${this._renderPath(scaledData, 0)}
        ${this._renderPath(scaledData, 1)}
        ${this._renderPath(scaledData, 2)}
      </g>
    `;
  }

  _renderPath(scaledData, warningLevel) {
    const className = ['good', 'warning', 'alert'][warningLevel];
    const strokeWidth = STROKE_WIDTHS[warningLevel];
    const pathTexts = this._generateDisjointedPathTexts(scaledData);

    return pathTexts.map((pathText) => svg`
      <path
        class="path ${className}"
        clip-path="url(#${className}-path)"
        stroke-width="${strokeWidth}"
        d="${pathText}"
      ></path>
    `);
  }

  _generateDisjointedPathTexts(scaledData) {
    const pathTexts = [];
    let pathText = '';
    for (let i = 0; i < scaledData.length; i++) {
      if (!pathText) {
        if (!Number.isFinite(scaledData[i].y)) {
          continue;
        }
        pathText = `M ${scaledData[i].x} ${scaledData[i].y}`;
      } else if (Number.isFinite(scaledData[i].y)) {
        pathText += `L${scaledData[i].x} ${scaledData[i].y}`;
      } else {
        pathTexts.push(pathText);
        pathText = '';
      }
    }
    pathTexts.push(pathText);
    return pathTexts;
  }

  _renderPathShadow(scaledData) {
    const pathTexts = this._generateDisjointedPathTexts(scaledData);

    return pathTexts.map((pathText) => svg`
      <path class="path shadow" d="${pathText}"></path>
    `);
  }

  _renderPointIndicators(scaledData) {
    return scaledData.map((current) => {
      if (current.composed || !Number.isFinite(current.y)) {
        return;
      }

      const radius = POINT_RADIUS[current.warningLevel];
      const selectionClasses = this.getSelectableClassList(current.event).join(' ');

      return svg`
        <circle
          cx="${current.x}"
          cy="${current.y}"
          r="${radius}"
          class="point shadow ${selectionClasses}"
        ></circle>
        <circle
          cx="${current.x}"
          cy="${current.y}"
          r="${radius}"
          class="point ${['good', 'warning', 'alert'][current.warningLevel]} ${selectionClasses}"
        ></circle>
      `;
    }).filter((ele) => ele);
  }

  _renderClipPaths(scaledData) {
    const { NO_REPORT_TOLERANCE_S, REPORT_CADENCE_S } = this.constants();

    const rectGroups = [[], [], []];
    const anyRects = [];

    const pushRect = ({ rightX, leftX, rectGroup }) => {
      const paddedRight = rightX + this.padding;
      const rect = svg`<rect x="${leftX - (this.padding / 2)}" y="0" width="${paddedRight - leftX}" height="400" />`;
      rectGroup.push(rect);
      anyRects.push(rect);
    };

    const scaleAdd = (date) => this.xScale(moment(date).add(REPORT_CADENCE_S / 2, 'seconds'));
    const scaleSubtract = (date) => this.xScale(moment(date).subtract(REPORT_CADENCE_S / 2, 'seconds'));

    let last = scaledData[0];
    let current;
    let rectGroup;
    let areaStart = last;

    for (let i = 1; i < scaledData.length; i++) {
      last = scaledData[i - 1];
      current = scaledData[i];
      rectGroup = rectGroups[areaStart.warningLevel];

      // End the current area if there is a gap in readings
      if (moment(last.event.date).diff(moment(current.event.date), 'seconds') > NO_REPORT_TOLERANCE_S) {
        pushRect({ rightX: scaleAdd(areaStart.event.date), leftX: scaleSubtract(last.event.date), rectGroup });
        areaStart = current;
      }

      // End the current area if the severity level changes
      if (current.warningLevel !== areaStart.warningLevel) {
        pushRect({ rightX: scaleAdd(areaStart.event.date), leftX: scaleSubtract(last.event.date), rectGroup });
        areaStart = current;
      }
    }

    // End whatever area we have started
    pushRect({ rightX: scaleAdd(areaStart.event.date), leftX: scaleSubtract(last.event.date), rectGroup });

    return svg`
      <clipPath id="any-path">${anyRects}</clipPath>
      <clipPath id="good-path">${rectGroups[0]}</clipPath>
      <clipPath id="warning-path">${rectGroups[1]}</clipPath>
      <clipPath id="alert-path">${rectGroups[2]}</clipPath>
    `;
  }
};
